import morphdom from "morphdom";

/**
 * Custom element for handling search queries and displaying results.
 * Listens for search-query events and fetches/displays results.
 * @class
 * @extends {HTMLElement}
 */
class MatrixQuery extends HTMLElement {
    /**
     * Initialize the matrix query component.
     * @constructor
     */
    constructor() {
        super();
        // No Shadow DOM
        /** @type {string} API endpoint for search queries */
        this.endpointUrl = this.getAttribute('endpoint') || '/search';
        /** @type {Function} Bound event handler for search queries */
        this.handleSearchQuery = this.handleSearchQuery.bind(this); // Bind method once
        /** @type {string} Original HTML content for potential restoration */
        this.originalContent = this.innerHTML; // Store initial content if needed
        /** @type {boolean} Flag to prevent concurrent requests */
        this.isLoading = false; // Flag to prevent concurrent requests
    }

    /**
     * Called when the element is inserted into the DOM.
     * Sets up event listeners for search-query events.
     */
    connectedCallback() {
        // Listen for the custom event on the document
        document.addEventListener('search-query', this.handleSearchQuery);
        console.log('SearchQuery component connected and listening for search-query event.');
    }

    /**
     * Called when the element is removed from the DOM.
     * Cleans up event listeners.
     */
    disconnectedCallback() {
        // Clean up the event listener
        document.removeEventListener('search-query', this.handleSearchQuery);
        console.log('SearchQuery component disconnected.');
    }

    /**
     * Handles search query events by fetching and displaying results.
     * @async
     * @param {CustomEvent} event - The search-query event
     * @returns {Promise<void>}
     */
    async handleSearchQuery(event) {
        if (this.isLoading) {
            console.log('SearchQuery: Request already in progress. Skipping.');
            return;
        }

        const queryData = event.detail;
        console.log('SearchQuery received event with data:', queryData);

        if (!queryData) {
            console.warn('SearchQuery: Received event with no data.');
            return;
        }

        this.isLoading = true;

        try {
            const response = await fetch(this.endpointUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/html' // Assuming the endpoint returns HTML
                },
                body: JSON.stringify(queryData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const resultsHtml = await response.text();

            morphdom(this, resultsHtml, {childrenOnly: true});  // Replace content with response HTML

        } catch (error) {
            console.error('SearchQuery: Error fetching search results:', error);
            // Display error message or restore original content
            this.innerHTML = `<p style="color: red;">Error loading search results: ${error.message}</p>`;
            // Optionally restore original content: this.innerHTML = this.originalContent;
        } finally {
            this.isLoading = false;
        }
    }
}

customElements.define('matrix-query', MatrixQuery);
